import { useQuery } from "@tanstack/react-query";
import { CourseJobStatus } from "@vixi/types";
import api from "@vixi/api";
import { logger } from "@vixi/utils";

const POLLING_INTERVAL = 3000; // 3 seconds

export const useCourseJobStatus = (jobId: string | null, enabled: boolean = true) => {
  const query = useQuery({
    queryKey: ["course-job-status", jobId],
    queryFn: async () => {
      if (!jobId) throw new Error("Job ID is required");

      // ============================================
      // STEP 2: GET /api/CourseMaker/jobs/{jobId}
      // ============================================
      // This is the SECOND request - polling every 3 seconds
      // Response: { jobId, status, progressPercentage, progressMessage, ... }
      // This runs repeatedly until job is completed (status !== 0)
      
      const endpoint = api.endpoints.getCourseJobStatus(jobId);
      const baseURL = api.getDefaultConfig().baseURL || "/api";
      const fullURL = `${baseURL}/${endpoint}`;
      
      logger.debug(`📥 STEP 2: Polling GET ${fullURL} (every ${POLLING_INTERVAL}ms)`);
      
      const response = await api.get<CourseJobStatus>(endpoint);
      
      logger.debug(`✅ STEP 2: Got job status - Progress: ${response.data.progressPercentage}%, Status: ${response.data.status}`);
      
      return response.data;
    },
    enabled: enabled && !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Stop polling if job is completed (completedAt is not null)
      // completedAt !== null means job has finished (successfully or with error)
      if (data && data.completedAt !== null) {
        logger.info(`✅ STEP 2 COMPLETE: Job finished (completedAt: ${data.completedAt}). Stopping polling.`);
        return false;
      }
      // Continue polling every 3 seconds while job is in progress
      return POLLING_INTERVAL;
    },
    retry: false,
  });

  return query;
};
